// experiences/Dodecahedron.jsx

import React, { useRef, useEffect, useState } from "react";
import {
  CameraControls,
  PerspectiveCamera,
  PresentationControls,
  useGLTF,
  Float,
  Environment,
  Html,
} from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import useStore from "../stores/store";
import * as THREE from "three";
import { Chamber } from "./Chamber/Chamber";

const S3_BUCKET_URL = "https://site-davidbickley.s3.us-east-2.amazonaws.com";

function FogControl({ theme }) {
  const { scene } = useThree();

  useEffect(() => {
    if (theme?.bodyBgPrimary) {
      scene.fog = new THREE.FogExp2(theme.bodyBgPrimary, 0.085);
    }
    return () => {
      scene.fog = null;
    };
  }, [scene, theme]);

  return null;
}

function MeshGroup({ instances, geometry, material }) {
  const meshes = useRef([]);

  useFrame((state, delta) => {
    meshes.current.forEach((mesh, index) => {
      if (mesh) {
        mesh.rotation.x += instances[index].rotationSpeed.x * delta;
        mesh.rotation.y += instances[index].rotationSpeed.y * delta;
        mesh.rotation.z += instances[index].rotationSpeed.z * delta;
      }
    });
  });

  return instances.map((instance, index) => (
    <mesh
      key={index}
      ref={(el) => (meshes.current[index] = el)}
      geometry={geometry}
      material={material}
      scale={instance.scale}
      position={instance.position}
      castShadow
      receiveShadow
    />
  ));
}

export default function Dodecahedron({
  isTransitioning,
  transitionValues,
  ...props
}) {
  const [loadError, setLoadError] = useState(null);
  const { setLoading } = useStore();
  const { theme } = useStore();

  // Add error handling to model loading
  const { nodes, errors } = useGLTF(
    `${S3_BUCKET_URL}/models/dodecahedronThinSmooth.glb`,
    true, // Load with error handling
    (error) => {
      console.error("Model loading error:", error);
      setLoadError(error.message);
      setLoading(false);
    }
  );

  useEffect(() => {
    props.onLoad?.();
  }, []);

  if (loadError) {
    return (
      <Html center>
        <div
          style={{
            color: "white",
            background: "rgba(0,0,0,0.7)",
            padding: "20px",
          }}
        >
          Error loading model: {loadError}
        </div>
      </Html>
    );
  }

  if (!nodes?.Solid005?.geometry) {
    console.error("Required geometry not found:", nodes);
    return null;
  }

  const material = new THREE.MeshStandardMaterial({
    color: theme.secondaryColor || "#ffffff",
    roughness: 0,
    metalness: 1,
    side: THREE.DoubleSide,
  });

  const instances = [
    {
      scale: 3,
      position: [0, 0, 0],
      rotationSpeed: { x: 0.05, y: 0.15, z: -0.15 },
    },
    {
      scale: 2.85,
      position: [0, 0, 0],
      rotationSpeed: { x: 0.05, y: 0.145, z: -0.075 },
    },
    {
      scale: 2.65,
      position: [0, 0, 0],
      rotationSpeed: { x: 0.05, y: 0.14, z: -0.1 },
    },
    {
      scale: 2.45,
      position: [0, 0, 0],
      rotationSpeed: { x: 0.05, y: 0.13, z: -0.1 },
    },
    {
      scale: 2.25,
      position: [0, 0, 0],
      rotationSpeed: { x: 0.05, y: 0.1, z: -0.05 },
    },
  ];

  const opacity = isTransitioning ? transitionValues?.opacity || 0 : 1;

  return (
    <group visible={opacity > 0}>
      <color attach="background" args={[theme.bodyBgPrimary || "#000000"]} />
      <PerspectiveCamera
        makeDefault
        position={[6, -1, 8]}
        fov={40}
        near={0.1}
        far={1000}
      />
      <ambientLight intensity={0.5} />
      <directionalLight intensity={2.5} position={[-5, 5, 5]} castShadow />
      <pointLight intensity={1} position={[5, 5, 5]} color="#ffffff" />
      <FogControl theme={theme} />
      <group position={[0, 0, 0]}>
        <Chamber position={[-1.5, -4, 3]} />
        <PresentationControls
          global={true}
          config={{ mass: 2, tension: 500 }}
          snap={{ mass: 4, tension: 250 }}
          rotation={[0, 0.3, 0]}
          polar={[-Math.PI / 3, Math.PI / 3]}
          azimuth={[-Math.PI / 1.4, Math.PI / 2]}
        >
          <Float
            speed={1}
            rotationIntensity={0}
            floatIntensity={0.5}
            floatingRange={[0, 0.5]}
          >
            <MeshGroup
              instances={instances}
              geometry={nodes.Solid005.geometry}
              material={material}
            />
          </Float>
        </PresentationControls>
      </group>
      <CameraControls
        minAzimuthAngle={-Math.PI / 6}
        maxAzimuthAngle={Math.PI / 3}
        minPolarAngle={Math.PI / 2}
        maxPolarAngle={Math.PI - Math.PI / 2.25}
        minDistance={8}
        maxDistance={12.5}
        smoothTime={0.5}
        truckSpeed={0}
      />
      <Environment
        files={`${S3_BUCKET_URL}/textures/environments/beachBridge.jpg`}
        environmentIntensity={0.5}
        backgroundIntensity={0}
      />
    </group>
  );
}

useGLTF.preload(`${S3_BUCKET_URL}/models/dodecahedronThinSmooth.glb`);
